import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';

import type { PatientPaymentMethod } from '../types';

/**
 * Get card brand display name
 */
function getCardBrandDisplay(brand?: string | null): string {
  if (!brand) return 'Card';

  const brandMap: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    diners: 'Diners',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };

  return brandMap[brand.toLowerCase()] || brand;
}

export interface PatientPaymentMethodCardProps {
  paymentMethod: PatientPaymentMethod;
  onSetDefault: (paymentMethodId: string) => Promise<void>;
  onDelete: (paymentMethodId: string) => Promise<void>;
  isSettingDefault?: boolean;
  isDeleting?: boolean;
}

/**
 * Individual payment method card displaying card/bank info with actions
 */
export function PatientPaymentMethodCard({
  paymentMethod,
  onSetDefault,
  onDelete,
  isSettingDefault = false,
  isDeleting = false,
}: PatientPaymentMethodCardProps) {
  const [localDeleting, setLocalDeleting] = useState(false);
  const [localSettingDefault, setLocalSettingDefault] = useState(false);

  const isCard = paymentMethod.type === 'card';
  const isDefault = paymentMethod.is_default;
  const isProcessing = localDeleting || localSettingDefault || isSettingDefault || isDeleting;

  const handleSetDefault = async () => {
    if (isDefault || isProcessing) return;

    setLocalSettingDefault(true);
    try {
      await onSetDefault(paymentMethod.id);
    } finally {
      setLocalSettingDefault(false);
    }
  };

  const handleDelete = async () => {
    if (isProcessing) return;

    setLocalDeleting(true);
    try {
      await onDelete(paymentMethod.id);
    } finally {
      setLocalDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: isDefault ? 'primary.light' : 'divider',
        backgroundColor: isDefault ? 'primary.50' : 'background.paper',
        '&:hover': {
          borderColor: isDefault ? 'primary.main' : 'action.hover',
        },
      }}
    >
      {/* Left: Icon and payment info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isCard ? (
          <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
        ) : (
          <BankIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
        )}

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {isCard ? getCardBrandDisplay(paymentMethod.card_brand) : 'Bank Account'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ****{paymentMethod.card_last4}
            </Typography>
            {isDefault && (
              <Chip
                label="Default"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {isCard && paymentMethod.card_exp_month && paymentMethod.card_exp_year && (
            <Typography variant="caption" color="text.secondary">
              Expires {String(paymentMethod.card_exp_month).padStart(2, '0')}/
              {String(paymentMethod.card_exp_year).slice(-2)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right: Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {!isDefault && (
          <Button
            size="small"
            variant="text"
            onClick={handleSetDefault}
            disabled={isProcessing}
            startIcon={
              localSettingDefault ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <StarBorderIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
          >
            Set Default
          </Button>
        )}

        <IconButton
          size="small"
          onClick={handleDelete}
          disabled={isProcessing}
          sx={{ color: 'error.main' }}
          aria-label="Delete payment method"
        >
          {localDeleting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <DeleteIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}

export default PatientPaymentMethodCard;
